/**
 * SEO Meta 标签管理
 * SEO meta tags management for multi-language support
 */

import { watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

/**
 * SEO Meta composable
 * @param {Object} options - Meta 配置选项
 * @param {Object} options.title - 页面标题（多语言对象）
 * @param {Object} options.description - 页面描述（多语言对象）
 * @param {Object} options.keywords - 关键词（多语言对象，可选）
 */
export function useSeoMeta(options = {}) {
  const { locale } = useI18n()
  const route = useRoute()
  
  const siteUrl = 'https://stoneit.com' // 替换为实际网站 URL
  const siteName = 'Global Data'
  const localeMetaMap = {
    zh: { htmlLang: 'zh-CN', hreflang: 'zh-CN', ogLocale: 'zh_CN' },
    en: { htmlLang: 'en', hreflang: 'en', ogLocale: 'en_US' },
    ja: { htmlLang: 'ja', hreflang: 'ja', ogLocale: 'ja_JP' },
    ko: { htmlLang: 'ko-KR', hreflang: 'ko-KR', ogLocale: 'ko_KR' }
  }
  
  /**
   * 更新页面标题
   */
  const updateTitle = (title) => {
    if (!title) return
    const localizedTitle = typeof title === 'object'
      ? title[locale.value] || title.en || title.zh || title.ja || title.ko
      : title
    document.title = localizedTitle ? `${localizedTitle} - ${siteName}` : siteName
  }
  
  /**
   * 更新或创建 meta 标签
   */
  const updateMetaTag = (name, content, attribute = 'name') => {
    if (!content) return
    
    let meta = document.querySelector(`meta[${attribute}="${name}"]`)
    if (!meta) {
      meta = document.createElement('meta')
      meta.setAttribute(attribute, name)
      document.head.appendChild(meta)
    }
    meta.setAttribute('content', content)
  }
  
  /**
   * 更新 hreflang 链接标签
   */
  const updateHreflangLinks = () => {
    // 移除旧的 hreflang 标签
    document.querySelectorAll('link[rel="alternate"]').forEach(link => link.remove())
    
    const currentPath = route.path.replace(/^\/(zh|en|ja|ko)/, '') || '/'
    const languages = ['zh', 'en', 'ja', 'ko']
    
    languages.forEach(lang => {
      const link = document.createElement('link')
      link.rel = 'alternate'
      link.hreflang = localeMetaMap[lang]?.hreflang || lang
      link.href = `${siteUrl}/${lang}${currentPath}`
      document.head.appendChild(link)
    })
    
    // 添加 x-default
    const defaultLink = document.createElement('link')
    defaultLink.rel = 'alternate'
    defaultLink.hreflang = 'x-default'
    defaultLink.href = `${siteUrl}${currentPath}`
    document.head.appendChild(defaultLink)
  }
  
  /**
   * 更新所有 SEO meta 标签
   */
  const updateSeoMeta = () => {
    const {title, description, keywords, image } = options
    
    // 获取本地化文本
    const getLocalized = (obj) => {
      if (!obj) return ''
      return typeof obj === 'object'
        ? obj[locale.value] || obj.en || obj.zh || obj.ja || obj.ko || ''
        : obj
    }
    
    const localizedTitle = getLocalized(title)
    const localizedDescription = getLocalized(description)
    const localizedKeywords = getLocalized(keywords)
    
    // 更新标题
    updateTitle(title)
    
    // 更新 HTML lang 属性
    document.documentElement.setAttribute('lang', localeMetaMap[locale.value]?.htmlLang || locale.value)
    
    // 基础 meta 标签
    updateMetaTag('description', localizedDescription)
    if (localizedKeywords) {
      updateMetaTag('keywords', localizedKeywords)
    }
    
    // Open Graph 标签
    updateMetaTag('og:title', localizedTitle, 'property')
    updateMetaTag('og:description', localizedDescription, 'property')
    updateMetaTag('og:type', 'website', 'property')
    updateMetaTag('og:url', `${siteUrl}${route.path}`, 'property')
    updateMetaTag('og:site_name', siteName, 'property')
    updateMetaTag('og:locale', localeMetaMap[locale.value]?.ogLocale || locale.value, 'property')
    
    if (image) {
      updateMetaTag('og:image', image, 'property')
    }
    
    // Twitter Card 标签
    updateMetaTag('twitter:card', 'summary_large_image')
    updateMetaTag('twitter:title', localizedTitle)
    updateMetaTag('twitter:description', localizedDescription)
    if (image) {
      updateMetaTag('twitter:image', image)
    }
    
    // 更新 hreflang 链接
    updateHreflangLinks()
  }
  
  // 监听语言变化和路由变化
  watch([locale, () => route.path], () => {
    updateSeoMeta()
  })
  
  // 初始化时更新
  onMounted(() => {
    updateSeoMeta()
  })
  
  return {
    updateSeoMeta
  }
}

export default useSeoMeta
